import {
    LEAGUE_REACTION_ACTIVITY_CAP,
    LEAGUE_REACTION_EMOJI_OPTIONS,
    LEAGUE_REACTION_GLYPH,
    type LeagueReactionEmoji,
} from "./leagueReactions.ts";
import type { League, LeagueMember, LeagueReactionActivity } from "./types.ts";

export {
    LEAGUE_REACTION_ACTIVITY_CAP,
    LEAGUE_REACTION_FEED_CAP,
} from "./leagueReactions.ts";

/** Prepend a throw onto the Activity list; skip duplicates and keep a short window. */
export function prependLeagueReactionEvent(
    events: LeagueReactionActivity[] | undefined,
    event: LeagueReactionActivity,
): LeagueReactionActivity[] {
    if (events?.some((row) => row.id === event.id)) return events;
    return [event, ...(events ?? [])].slice(0, LEAGUE_REACTION_ACTIVITY_CAP);
}

export function leagueReactionActivityFromThrow(input: {
    reactionId: string;
    members: Pick<LeagueMember, "userId" | "playerName">[];
    fromUserId: number;
    fromPlayerName?: string;
    targetUserId: number;
    emoji: LeagueReactionEmoji;
    at?: string;
}): LeagueReactionActivity | null {
    const { reactionId, members, fromUserId, targetUserId, emoji } = input;
    if (!reactionId || fromUserId === targetUserId) return null;
    const from = members.find((m) => m.userId === fromUserId);
    const target = members.find((m) => m.userId === targetUserId);
    return {
        id: reactionId,
        at: input.at ?? new Date().toISOString(),
        fromUserId,
        fromPlayerName: from?.playerName?.trim() || input.fromPlayerName?.trim() || `Player ${fromUserId}`,
        targetUserId,
        targetPlayerName: target?.playerName?.trim() || `Player ${targetUserId}`,
        emoji,
    };
}

export type LeagueMemberReactionStats = {
    sent: number;
    received: Record<LeagueReactionEmoji, number>;
};

export function emptyMemberReactions(): LeagueMemberReactionStats {
    return {
        sent: 0,
        received: { poop: 0, rofl: 0, rocket: 0, fire: 0, clap: 0, cry: 0 },
    };
}

export function normalizeMemberReactions(
    raw: LeagueMember["reactions"] | null | undefined,
): LeagueMemberReactionStats {
    const base = emptyMemberReactions();
    if (!raw || typeof raw !== "object") return base;
    const sent = Math.max(0, Math.floor(Number(raw.sent) || 0));
    const received = { ...base.received };
    for (const emoji of LEAGUE_REACTION_EMOJI_OPTIONS) {
        received[emoji] = Math.max(0, Math.floor(Number(raw.received?.[emoji]) || 0));
    }
    return { sent, received };
}

export function memberReactionsReceivedTotal(stats: LeagueMemberReactionStats): number {
    return LEAGUE_REACTION_EMOJI_OPTIONS.reduce((sum, emoji) => sum + stats.received[emoji], 0);
}

/** Emoji this member has received the most of (null if none). */
export function memberTopReceivedEmoji(
    stats: LeagueMemberReactionStats,
): LeagueReactionEmoji | null {
    let best: LeagueReactionEmoji | null = null;
    let bestCount = 0;
    for (const emoji of LEAGUE_REACTION_EMOJI_OPTIONS) {
        const n = stats.received[emoji];
        if (n > bestCount) {
            best = emoji;
            bestCount = n;
        }
    }
    return bestCount > 0 ? best : null;
}

export function applyMemberReactionSent(
    stats: LeagueMemberReactionStats,
): LeagueMemberReactionStats {
    return { ...stats, sent: stats.sent + 1 };
}

export function applyMemberReactionReceived(
    stats: LeagueMemberReactionStats,
    emoji: LeagueReactionEmoji,
): LeagueMemberReactionStats {
    return {
        ...stats,
        received: { ...stats.received, [emoji]: stats.received[emoji] + 1 },
    };
}

/** Bump sender sent + target received on a league snapshot (idempotent at the call site). */
export function applyLeagueReactionToMembers<T extends { userId: number; reactions?: LeagueMember["reactions"] }>(
    members: T[],
    input: { fromUserId: number; targetUserId: number; emoji: LeagueReactionEmoji },
): T[] {
    const { fromUserId, targetUserId, emoji } = input;
    if (fromUserId === targetUserId) return members;
    return members.map((member) => {
        if (member.userId === fromUserId) {
            return { ...member, reactions: applyMemberReactionSent(normalizeMemberReactions(member.reactions)) };
        }
        if (member.userId === targetUserId) {
            return {
                ...member,
                reactions: applyMemberReactionReceived(normalizeMemberReactions(member.reactions), emoji),
            };
        }
        return member;
    });
}

export type LeagueReactionCalloutKind =
    | "roasted"
    | "laughed"
    | "hyped"
    | "on_fire"
    | "respected"
    | "saltiest";

export type LeagueReactionCallout = {
    kind: LeagueReactionCalloutKind;
    emoji: LeagueReactionEmoji;
    label: string;
    count: number;
    member: Pick<
        LeagueMember,
        "userId" | "playerName" | "avatar" | "xpWeekCrown" | "avatarFrameUrl" | "avatarFrameSmallUrl"
    >;
};

const CALLOUT_DEFS: Array<{
    kind: LeagueReactionCalloutKind;
    emoji: LeagueReactionEmoji;
    label: string;
}> = [
    { kind: "roasted", emoji: "poop", label: "Most roasted" },
    { kind: "laughed", emoji: "rofl", label: "Most laughed at" },
    { kind: "hyped", emoji: "rocket", label: "Most hyped" },
    { kind: "on_fire", emoji: "fire", label: "Most on fire" },
    { kind: "respected", emoji: "clap", label: "Most respected" },
    { kind: "saltiest", emoji: "cry", label: "Most salt" },
];

/** Max awards on end cards / share / OG — one per emoji type. */
export const LEAGUE_REACTION_AWARD_CAP = CALLOUT_DEFS.length;

/**
 * Ended-league fun callouts — one winner per emoji (ties: lower userId).
 * Omitted when nobody has received that emoji yet.
 * Sorted by count desc so the loudest banter leads; every emoji with receives is included.
 */
export function leagueReactionCallouts(league: League): LeagueReactionCallout[] {
    const out: LeagueReactionCallout[] = [];
    for (const def of CALLOUT_DEFS) {
        let winner: LeagueMember | null = null;
        let best = 0;
        for (const member of league.members) {
            const n = normalizeMemberReactions(member.reactions).received[def.emoji];
            if (n <= 0) continue;
            if (
                n > best ||
                (n === best && winner != null && member.userId < winner.userId) ||
                (n === best && winner == null)
            ) {
                best = n;
                winner = member;
            }
        }
        if (!winner || best <= 0) continue;
        out.push({
            kind: def.kind,
            emoji: def.emoji,
            label: def.label,
            count: best,
            member: {
                userId: winner.userId,
                playerName: winner.playerName,
                avatar: winner.avatar,
                avatarFrameUrl: winner.avatarFrameUrl ?? null,
                avatarFrameSmallUrl: winner.avatarFrameSmallUrl ?? null,
                xpWeekCrown: winner.xpWeekCrown,
            },
        });
    }
    return out.sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.member.userId - b.member.userId;
    });
}

export function reactionCalloutGlyph(emoji: LeagueReactionEmoji): string {
    return LEAGUE_REACTION_GLYPH[emoji];
}

export type LeagueReceivedStackItem = {
    emoji: LeagueReactionEmoji;
    count: number;
};

/** Top received emojis for a leaderboard stack (highest count first). */
export function memberReceivedStack(
    stats: LeagueMemberReactionStats | LeagueMember["reactions"] | null | undefined,
    limit = 3,
): LeagueReceivedStackItem[] {
    const normalized = normalizeMemberReactions(stats);
    const take = Math.max(0, Math.floor(limit));
    return LEAGUE_REACTION_EMOJI_OPTIONS.map((emoji) => ({
        emoji,
        count: normalized.received[emoji],
    }))
        .filter((row) => row.count > 0)
        .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.emoji.localeCompare(b.emoji);
        })
        .slice(0, take);
}

export type LeagueViewerBanterSummary = {
    sent: number;
    receivedTotal: number;
    stack: LeagueReceivedStackItem[];
};

/** Viewer's throw/receive summary for Mine / ended surfaces. */
export function leagueViewerBanterSummary(league: League): LeagueViewerBanterSummary | null {
    const me = league.members.find((m) => m.isViewer);
    if (!me) return null;
    const stats = normalizeMemberReactions(me.reactions);
    const receivedTotal = memberReactionsReceivedTotal(stats);
    if (stats.sent <= 0 && receivedTotal <= 0) return null;
    return {
        sent: stats.sent,
        receivedTotal,
        stack: memberReceivedStack(stats, 4),
    };
}

/** Seconds a live banter callout stays on one award before rotating. */
export const LEAGUE_BANTER_CALLOUT_ROTATE_MS = 5_500;

/** Heat streak window for UNDER FIRE / ON FIRE row badges. */
export const LEAGUE_REACTION_HEAT_WINDOW_MS = 8_000;
export const LEAGUE_REACTION_HEAT_MIN_HITS = 3;
